import React, { useState, useEffect } from 'react';
import {
  Menu,
  AppBar,
  Toolbar,
  Typography,
  IconButton,
  Badge,
  Avatar,
  MenuItem,
  Drawer,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Box,
  Divider,
  Tooltip,
  useTheme,
  useMediaQuery,
  Chip,
  Button,
  Stack
} from '@mui/material';
import {
  Menu as MenuIcon,
  ChevronLeft,
  Dashboard,
  Business,
  Pets,
  SwapHoriz,
  NotificationsActive,
  ListAlt,
  HealthAndSafety,
  ReceiptLong,
  Tag,
  Psychology,
  AdminPanelSettings,
  Chat,
  Phone,
  Person as ProfileIcon,
  Logout,
  AppShortcut,
  Notifications,
  Agriculture,
  Assessment,
  AutoDelete,
  CleaningServices,
  Mic
} from '@mui/icons-material';
import { useNavigate, useLocation, Outlet } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useVoice } from '../contexts/VoiceContext';
import { useSocket } from '../contexts/SocketContext';
import { TrendingUp } from '@mui/icons-material';

const Layout = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, logout } = useAuth();
  const { startListening, stopListening, isListening, isSupported, language, changeLanguage, reinitialize } = useVoice();
  const { isConnected } = useSocket();

  useEffect(() => {
    const TARGET_VERSION = 'v1.1.0';
    const currentVersion = localStorage.getItem('baserri_v');
    if (currentVersion !== TARGET_VERSION) {
      console.log(`[NUCLEAR] Purging cache... Migrating to ${TARGET_VERSION}`);
      if ('caches' in window) {
        caches.keys().then(names => Promise.all(names.map(n => caches.delete(n))));
      }
      localStorage.setItem('baserri_v', TARGET_VERSION);
      // Hard refresh to clear memory and force new SW/Assets
      if (currentVersion) {
         window.location.reload(true);
      }
    }
  }, []);
  
  const [anchorEl, setAnchorEl] = useState(null);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [notifications, setNotifications] = useState([
    { id: 1, title: 'Alerta sanitaria', message: 'Vacunación pendiente', type: 'warning' },
    { id: 2, title: 'Guía de salida', message: 'Animal ES 48 1234567891 listo', type: 'info' }
  ]);

  const handleMenu = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const handleVoiceToggle = () => {
    if (isListening) {
      stopListening();
    } else {
      startListening();
    }
  };

  const handleHardPurge = async () => {
    if (window.confirm('¿Deseas realizar una limpieza profunda? Esto cerrará la sesión y reiniciará la aplicación para solucionar errores persistentes de conexión.')) {
      try {
        // 1. Unregister all Service Workers
        if ('serviceWorker' in navigator) {
          const registrations = await navigator.serviceWorker.getRegistrations();
          for (const registration of registrations) {
            await registration.unregister();
          }
        }
        // 2. Clear all Caches
        if ('caches' in window) {
          const cacheNames = await caches.keys();
          for (const name of cacheNames) {
            await caches.delete(name);
          }
        }
        // 3. Clear storage
        localStorage.clear();
        sessionStorage.clear();
        // 4. Force reload from server
        window.location.href = '/login?purged=true';
      } catch (err) {
        console.error('Purge failed:', err);
        window.location.reload(true);
      }
    }
  };

  const { messages: socketMessages } = useSocket();
  const newMessagesCount = socketMessages.filter(m => m.type === 'ganadero').length;

  const menuItems = [
    { icon: <Assessment />, label: 'Resumen', path: '/app/resumen' },
    { icon: <Dashboard />, label: 'Dashboard', path: '/app/dashboard' },
    { icon: <Business />, label: 'Explotación', path: '/app/explotacion' },
    { icon: <Pets />, label: 'Animales', path: '/app/animales' },
    { icon: <SwapHoriz />, label: 'Movimientos', path: '/app/movimientos' },
    { icon: <NotificationsActive />, label: 'Alertas', path: '/app/alertas' },
    { icon: <ListAlt />, label: 'Censo', path: '/app/censo' },
    { icon: <HealthAndSafety />, label: 'Salud', path: '/app/salud' },
    { icon: <ReceiptLong />, label: 'Guías', path: '/app/guias' },
    { icon: <Tag />, label: 'Crotales', path: '/app/crotales' },
    { icon: <Psychology />, label: 'Asistente IA', path: '/app/asistente' },
    { icon: <AdminPanelSettings />, label: 'Administración', path: '/app/admin' },
    { 
      icon: (
        <Badge badgeContent={newMessagesCount} color="error">
          <Chat />
        </Badge>
      ), 
      label: 'Mensajería', 
      path: '/app/chat' 
    },
    { icon: <Phone />, label: 'Llamadas', path: '/app/phone' },
  ];

  return (
    <Box sx={{ flexGrow: 1 }}>
      <AppBar 
        position="fixed" 
        sx={{ 
          zIndex: (theme) => theme.zIndex.drawer + 1,
          background: 'rgba(255, 255, 255, 0.7)',
          backdropFilter: 'blur(20px)',
          borderBottom: '1px solid rgba(0,0,0,0.06)',
          color: 'var(--navy-main)',
          boxShadow: 'none'
        }}
      >
        <Toolbar sx={{ justifyContent: 'space-between' }}>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <IconButton
              size="large"
              edge="start"
              color="inherit"
              aria-label="menu"
              sx={{ mr: 2 }}
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            >
              <MenuIcon />
            </IconButton>
            
            <Typography 
              variant="h5" 
              component="div" 
              sx={{ 
                fontWeight: 900, 
                color: 'var(--forest-main)',
                fontFamily: 'Montserrat',
                letterSpacing: '-0.04em',
                display: 'flex',
                alignItems: 'center',
                gap: 1
              }}
            >
              <Agriculture fontSize="large" /> BASERRI-ADITU
            </Typography>
          </Box>

          {/* Recovery & Language Actions */}
          <Stack direction="row" spacing={1} alignItems="center" sx={{ mr: 2 }}>
            <Tooltip title="Limpieza profunda (Corregir errores de conexión)">
              <IconButton onClick={handleHardPurge} size="small" sx={{ color: 'var(--slate-light)' }}>
                <AutoDelete fontSize="small" />
              </IconButton>
            </Tooltip>
            <Chip 
              label={language?.split('-')[0].toUpperCase() || 'ES'} 
              onClick={() => changeLanguage(language === 'es-ES' ? 'eu' : 'es')}
              onDelete={reinitialize}
              deleteIcon={<TrendingUp sx={{ transform: 'rotate(90deg)', fontSize: '14px' }} />}
              sx={{ fontWeight: 800, bgcolor: 'white', border: '1px solid var(--border-color)' }}
            />
          </Stack>

          {/* Voice Assistant Status & Button */}
          {isSupported && (
            <Stack direction="row" spacing={1} alignItems="center">
              {isListening && (
                <Chip 
                  label={language?.startsWith('es') ? 'ESCUCHANDO...' : 'ENTZUTEN...'} 
                  color="error" 
                  size="small" 
                  sx={{ 
                    fontWeight: 900, 
                    animation: 'pulse 1.5s infinite',
                    '@keyframes pulse': {
                      '0%': { opacity: 1, transform: 'scale(1)' },
                      '50%': { opacity: 0.8, transform: 'scale(1.05)' },
                      '100%': { opacity: 1, transform: 'scale(1)' }
                    }
                  }} 
                />
              )}
              <Tooltip title={isListening ? 'Detener escucha' : 'Asistente de voz'}>
                <IconButton
                  color="inherit"
                  onClick={handleVoiceToggle}
                  sx={{
                    bgcolor: isListening ? '#ffeeee' : 'transparent',
                    '&:hover': { bgcolor: isListening ? '#ffdddd' : 'rgba(0,0,0,0.04)' }
                  }}
                >
                  <Mic sx={{ color: isListening ? '#ff4444' : 'inherit', fontSize: isListening ? 28 : 24 }} />
                </IconButton>
              </Tooltip>
            </Stack>
          )}

          {/* Phone Call Button */}
          <Tooltip title="Llamar a soporte">
            <IconButton color="inherit" onClick={() => navigate('/phone')}>
              <Phone />
            </IconButton>
          </Tooltip>

          {/* Notifications */}
          <Tooltip title="Notificaciones">
            <IconButton color="inherit" onClick={handleMenu}>
              <Badge badgeContent={notifications.length} color="error">
                <Notifications />
              </Badge>
            </IconButton>
          </Tooltip>
          
          <Menu
            id="menu-appbar"
            anchorEl={anchorEl}
            anchorOrigin={{
              vertical: 'top',
              horizontal: 'right',
            }}
            keepMounted
            transformOrigin={{
              vertical: 'top',
              horizontal: 'right',
            }}
            open={Boolean(anchorEl)}
            onClose={handleClose}
          >
            {notifications.map((notification) => (
              <MenuItem key={notification.id} onClick={handleClose}>
                <Box>
                  <Typography variant="subtitle2">{notification.title}</Typography>
                  <Typography variant="body2" color="text.secondary">
                    {notification.message}
                  </Typography>
                </Box>
              </MenuItem>
            ))}
          </Menu>

          {/* User Profile */}
          <Box sx={{ display: 'flex', alignItems: 'center', ml: 2 }}>
            <Chip
              avatar={<Avatar sx={{ bgcolor: 'var(--emerald-main)' }}>{user?.nombre?.[0]}</Avatar>}
              label={`${user?.nombre} ${user?.apellido}`}
              variant="outlined"
              sx={{ 
                color: 'var(--slate-dark)', 
                borderColor: 'var(--border-color)',
                fontWeight: 600,
                bgcolor: 'rgba(5, 150, 105, 0.05)'
              }}
            />
            <IconButton color="inherit" onClick={handleLogout} sx={{ ml: 1, color: 'var(--slate-light)' }}>
              <Logout />
            </IconButton>
          </Box>

          {/* Connection Status */}
          <Box
            sx={{
              width: 8,
              height: 8,
              borderRadius: '50%',
              backgroundColor: isConnected ? '#10b981' : '#ef4444',
              ml: 1
            }}
          />
        </Toolbar>
      </AppBar>

      {/* Mobile Menu Drawer */}
      {mobileMenuOpen && (
        <Box
          className="glass-panel"
          sx={{
            position: 'fixed',
            top: 64,
            left: 0,
            width: 280,
            height: 'calc(100vh - 64px)',
            backgroundColor: 'rgba(255, 255, 255, 0.95)',
            zIndex: 1000,
            borderRight: '1px solid var(--border-color)',
            overflow: 'auto',
            p: 1
          }}
        >
          {menuItems.map((item) => (
            <Button
              key={item.path}
              fullWidth
              startIcon={item.icon}
              onClick={() => {
                navigate(item.path);
                setMobileMenuOpen(false);
              }}
              sx={{
                justifyContent: 'flex-start',
                py: 1.5,
                px: 3,
                my: 0.5,
                borderRadius: 2,
                fontFamily: '"Inter", sans-serif',
                fontWeight: location.pathname === item.path ? 700 : 500,
                backgroundColor: location.pathname === item.path ? 'rgba(5, 150, 105, 0.08)' : 'transparent',
                color: location.pathname === item.path ? 'var(--emerald-dark)' : 'var(--slate-light)',
                '&:hover': {
                  backgroundColor: 'rgba(5, 150, 105, 0.04)',
                  color: 'var(--emerald-main)'
                }
              }}
            >
              {item.label}
            </Button>
          ))}
        </Box>
      )}

      {/* Main Content Area */}
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          p: { xs: 2, sm: 3 },
          mt: 8 // Adjust for fixed AppBar
        }}
      >
        <Outlet />
      </Box>
    </Box>
  );
};


export default Layout;
